// @owners { team: patients-team }
import { Experimentation } from '@alto/experimentation';
import { type DateRestrictionReason } from '@alto/scriptdash/alto/scheduling/types/v1/date_restriction_reason';
import { addDays, isAfter, parse } from 'date-fns';
import { useMemo } from 'react';
import { useNextAvailableDatesByPrescriptionIdFromQueryCache } from '~shared/features/next-available-date/queries/useNextAvailableDatesByPrescriptionIdFromQueryCache';
import { getPrescriptionsByIDs } from '~shared/features/prescriptions/selectors/getPrescriptionByID';
import { useSelectorShared } from '~shared/store';

export type BackorderedMedications = {
  medicationNames: string[];
  nextAvailableDateReasons: DateRestrictionReason[];
  nextAvailableDateThreshold: number;
};

const isBackOrderedMedicationName = (
  prescriptionName: string | undefined,
  medicationNames: string[] | undefined,
): boolean => {
  return (
    medicationNames?.some((medicationName) => prescriptionName?.toLowerCase().includes(medicationName.toLowerCase())) ??
    false
  );
};

const isWithinReason = (reason: string | undefined, reasons: string[]): boolean => {
  return reason ? reasons?.includes(reason) : false;
};

export const useBackorderedMedication = (prescriptionId: number | undefined): boolean => {
  const prescriptionIds = useMemo(() => (prescriptionId ? [prescriptionId] : []), [prescriptionId]);
  const backorderedMedications = useBackorderedMedications(prescriptionIds);
  return backorderedMedications?.length > 0;
};

export const useBackorderedMedications = (prescriptionIds: number[]): number[] => {
  const { value } = Experimentation.useFeatureFlag('backordered_medications', {});
  const nextAvailableDatesByPrescriptionID = useNextAvailableDatesByPrescriptionIdFromQueryCache();
  const prescriptions = useSelectorShared((state) => getPrescriptionsByIDs(state, prescriptionIds));

  return useMemo(() => {
    return prescriptions
      .filter((prescription) => {
        const availableDate = nextAvailableDatesByPrescriptionID[prescription.id];
        if (!availableDate?.earliest.date) return false;

        const isBackordered = isBackOrderedMedicationName(
          prescription.medication_name,
          value.medicationNames as string[],
        );
        if (!isBackordered) return false;

        const hasReason = isWithinReason(
          availableDate?.earliest?.reason,
          value.nextAvailableDateReasons as DateRestrictionReason[],
        );
        if (!hasReason) return false;

        const threshold = addDays(new Date(), (value.nextAvailableDateThreshold as number) || 0);
        return isAfter(parse(availableDate?.earliest.date, 'yyyy-MM-dd', new Date()), threshold);
      })
      .map((prescription) => prescription.id);
  }, [nextAvailableDatesByPrescriptionID, prescriptions, value]);
};
